import { useState, useCallback } from 'react';
import apiClient from '../services/api';
import { useNotification } from '../context/NotificationContext';

export function useProductSearch(productsPerPage = 100, locationId = null) {
  const [searchKeywords, setSearchKeywords] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchResultsCount, setSearchResultsCount] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const notification = useNotification();

  const searchProducts = useCallback(
    async (keywords = searchKeywords) => {
      if (!keywords.trim()) return;
      if (!locationId) {
        notification.error('Please select a location first.');
        return;
      }

      setIsSearching(true);
      try {
        const response = await apiClient.get(
          `/api/products/search/${keywords}/location/${locationId}`
        );

        setSearchResults(response.data);
        setSearchResultsCount(response.data.length);
        setCurrentPage(1);
      } catch (error) {
        console.error('Error searching products:', error);
        notification.error('Error occurred while searching products.');
      } finally {
        setIsSearching(false);
      }
    },
    [searchKeywords, locationId, notification]
  );

  const getCurrentPageProducts = useCallback(() => {
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    return searchResults.slice(indexOfFirstProduct, indexOfLastProduct);
  }, [currentPage, productsPerPage, searchResults]);

  const nextPage = useCallback(() => {
    if (currentPage < Math.ceil(searchResults.length / productsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, productsPerPage, searchResults.length]);

  const prevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage]);

  return {
    searchKeywords,
    setSearchKeywords,
    searchResults,
    searchResultsCount,
    currentPage,
    isSearching,
    searchProducts,
    getCurrentPageProducts,
    nextPage,
    prevPage,
    totalPages: Math.ceil(searchResults.length / productsPerPage),
  };
}
